// The station-agnostic half of the publisher-admin gate: is this caller
// signed in, and do they hold an active self-serve admin grant?
//
// Extracted in CTV-759 so the aggregated shell (/admin/all) can reuse the
// same two checks. It deliberately does NOT resolve a station: aggregated
// mode has no slug to resolve, and PublisherAdminGate layers the config
// fetch and canAdminStation check on top for single-station mode.
//
// Sign-in lives only at /admin. Deep-links are preserved via location state:
// `state.from` carries the original path through the sign-in round trip;
// AdminEntryPage redirects back there after the user authenticates.

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth, type SelfServiceUser } from '../auth/useAuth';

interface Props {
  children: (user: SelfServiceUser) => React.ReactNode;
}

const AdminAuthGate: React.FC<Props> = ({ children }) => {
  const location = useLocation();
  const { user, isAuthenticated, hasPublisherAdminGrant } = useAuth();

  if (!isAuthenticated || !user) {
    return (
      <Navigate
        to="/admin"
        state={{ from: location.pathname + location.search }}
        replace
      />
    );
  }

  // CTV-415 Phase 2b grant gate. Super_admins bypass entirely (they implicitly
  // hold the grant; the RPC short-circuits on is_super_admin). Other staff
  // need an active selfservice_publisher_admin_grants row issued by a
  // super_admin from the dashboard's Settings → Users. The RPC errors deny
  // by default (useAuth sets `false` on error) so a transient supabase outage
  // routes staff to /access-denied instead of accidentally letting them in.
  if (user.rawRole !== 'super_admin' && hasPublisherAdminGrant === false) {
    return <Navigate to="/access-denied" replace />;
  }

  return <>{children(user)}</>;
};

export default AdminAuthGate;
